// Auto-classify a set of option/stock legs into a recognisable strategy
// name — matches the "Long Call", "Iron Condor", "Double Diagonal" labels
// OptionStrat shows above the strategy header. Pure function, no state.
//
// Input legs: [{action:'BUY'|'SELL', type:'call'|'put'|'stock', strike, dte, qty}]
// Output: { name: string, hint?: string }
//
// Heuristics roughly in order of specificity. When no match → "Custom Strategy".

const sortByStrike = (arr) => [...arr].sort((a, b) => a.strike - b.strike);

const sig = (l) => `${l.action}-${l.type}-${l.qty || 1}`;
const sameExp = (a, b) => Math.abs((a.dte || 0) - (b.dte || 0)) < 1;
const sameStrike = (a, b) => Math.abs((a.strike || 0) - (b.strike || 0)) < 0.01;

export const classifyStrategy = (legs = []) => {
  if (!legs?.length) return { name: "Pick a strategy" };

  // Split by type
  const opts = legs.filter((l) => l.type === "call" || l.type === "put");
  const stocks = legs.filter((l) => l.type === "stock");
  const calls = sortByStrike(opts.filter((l) => l.type === "call"));
  const puts  = sortByStrike(opts.filter((l) => l.type === "put"));
  const longCalls  = calls.filter((l) => l.action === "BUY");
  const shortCalls = calls.filter((l) => l.action === "SELL");
  const longPuts   = puts.filter((l) => l.action === "BUY");
  const shortPuts  = puts.filter((l) => l.action === "SELL");
  const nLegs = opts.length;
  const expSet = new Set(opts.map((l) => Math.round(l.dte || 0)));
  const multiExp = expSet.size > 1;

  // ── 1 leg ──────────────────────────────────────────────────────
  if (nLegs === 1 && !stocks.length) {
    const l = opts[0];
    if (l.type === "call" && l.action === "BUY")  return { name: "Long Call" };
    if (l.type === "call" && l.action === "SELL") return { name: "Short Call" };
    if (l.type === "put"  && l.action === "BUY")  return { name: "Long Put" };
    if (l.type === "put"  && l.action === "SELL") return { name: "Cash-Secured Put" };
  }

  // ── stock + 1 option (covered/protective) ──────────────────────
  if (stocks.length === 1 && nLegs === 1) {
    const s = stocks[0];
    const l = opts[0];
    if (s.action === "BUY" && l.type === "call" && l.action === "SELL") return { name: "Covered Call" };
    if (s.action === "BUY" && l.type === "put"  && l.action === "BUY")  return { name: "Protective Put" };
  }

  // ── 2 legs ─────────────────────────────────────────────────────
  if (nLegs === 2 && !stocks.length) {
    // Vertical Call Spread
    if (calls.length === 2 && !multiExp) {
      if (calls[0].action === "BUY" && calls[1].action === "SELL")
        return { name: "Bull Call Spread" };
      if (calls[0].action === "SELL" && calls[1].action === "BUY")
        return { name: "Bear Call Spread" };
    }
    // Vertical Put Spread
    if (puts.length === 2 && !multiExp) {
      if (puts[0].action === "BUY" && puts[1].action === "SELL")
        return { name: "Bear Put Spread" };
      if (puts[0].action === "SELL" && puts[1].action === "BUY")
        return { name: "Bull Put Spread" };
    }
    // Calendar / Diagonal
    if (calls.length === 2 && multiExp) {
      if (sameStrike(calls[0], calls[1])) return { name: "Calendar Call Spread" };
      return { name: "Diagonal Call Spread" };
    }
    if (puts.length === 2 && multiExp) {
      if (sameStrike(puts[0], puts[1])) return { name: "Calendar Put Spread" };
      return { name: "Diagonal Put Spread" };
    }
    // Straddle / Strangle
    if (calls.length === 1 && puts.length === 1 && !multiExp) {
      const lc = calls[0];
      const lp = puts[0];
      const same = sameStrike(lc, lp);
      if (lc.action === "BUY" && lp.action === "BUY") {
        return { name: same ? "Long Straddle" : "Long Strangle" };
      }
      if (lc.action === "SELL" && lp.action === "SELL") {
        return { name: same ? "Short Straddle" : "Short Strangle" };
      }
      // Risk reversal / synthetic long-stock
      if (lc.action === "BUY" && lp.action === "SELL") return { name: "Synthetic Long" };
      if (lc.action === "SELL" && lp.action === "BUY") return { name: "Synthetic Short" };
    }
  }

  // ── 3 legs (butterflies) ───────────────────────────────────────
  if (nLegs === 3 && !multiExp) {
    if (calls.length === 3) {
      // 1 long lower, 2 short middle, 1 long upper — wait that's 4. Pure 3-leg ratio is:
      const [a, b, c] = calls;
      if (a.action === "BUY" && b.action === "SELL" && c.action === "BUY"
          && (b.qty || 1) === ((a.qty || 1) + (c.qty || 1)))
        return { name: "Long Call Butterfly" };
    }
    if (puts.length === 3) {
      const [a, b, c] = puts;
      if (a.action === "BUY" && b.action === "SELL" && c.action === "BUY"
          && (b.qty || 1) === ((a.qty || 1) + (c.qty || 1)))
        return { name: "Long Put Butterfly" };
    }
  }

  // ── 4 legs ─────────────────────────────────────────────────────
  if (nLegs === 4) {
    // Iron Condor: long lower put + short higher put + short lower call + long higher call
    if (puts.length === 2 && calls.length === 2 && !multiExp) {
      const [lp1, lp2] = puts;
      const [lc1, lc2] = calls;
      if (lp1.action === "BUY" && lp2.action === "SELL"
          && lc1.action === "SELL" && lc2.action === "BUY") {
        if (sameStrike(lp2, lc1)) return { name: "Iron Butterfly" };
        return { name: "Iron Condor" };
      }
      // Inverse
      if (lp1.action === "SELL" && lp2.action === "BUY"
          && lc1.action === "BUY" && lc2.action === "SELL") {
        if (sameStrike(lp2, lc1)) return { name: "Inverse Iron Butterfly" };
        return { name: "Inverse Iron Condor" };
      }
    }
    // Long Call Condor: 4 calls — long, short, short, long
    if (calls.length === 4) {
      const acts = calls.map((l) => l.action);
      if (acts.join("|") === "BUY|SELL|SELL|BUY") return { name: "Long Call Condor" };
      if (acts.join("|") === "SELL|BUY|BUY|SELL") return { name: "Short Call Condor" };
    }
    if (puts.length === 4) {
      const acts = puts.map((l) => l.action);
      if (acts.join("|") === "BUY|SELL|SELL|BUY") return { name: "Long Put Condor" };
      if (acts.join("|") === "SELL|BUY|BUY|SELL") return { name: "Short Put Condor" };
    }
    // Double Diagonal: 4 legs across 2 expirations — usually long P/C inner, short P/C outer
    if (multiExp && calls.length === 2 && puts.length === 2) {
      return { name: "Double Diagonal" };
    }
  }

  // ── Default ───────────────────────────────────────────────────
  if (nLegs === 0 && stocks.length) return { name: "Stock Position" };
  return { name: "Custom Strategy" };
};
